/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2024 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import {
    ApiHandler,
    createHandlerChainRunner,
    HasOnError,
    HasOnSuccess,
    markSkipOnCompleteIfOnErrorIsSet,
} from "@insite/client-framework/HandlerCreator";
import { ServiceResult } from "@insite/client-framework/Services/ApiService";
import getProducts from "@stoneage/App/Handlers/Pages/ProductDetails/GetProducts";
import { BomModel, getBom, GetBomApiParameter } from "@stoneage/App/Services/PageAccess/ProductBomService";

type HandlerType = ApiHandler<
    GetBomApiParameter & HasOnSuccess<ServiceResult<BomModel>> & HasOnError<string>,
    ServiceResult<BomModel>
>;
export const PopulateApiParameter: HandlerType = props => {
    props.apiParameter = { ...props.parameter };
    (props as any).key = props.apiParameter.productNumber;
};

export const DispatchBeginLoadProductBom: HandlerType = props => {
    props.dispatch({
        type: "xProductBom/BeginLoadProductBom",
        key: (props as any).key,
    } as any);
};

export const GetBom: HandlerType = async props => {
    const { onSuccess, onError, ...apiParameter } = props.apiParameter;
    props.apiResult = await getBom(apiParameter);
};

export const DispatchCompleteLoadProductBom: HandlerType = props => {
    props.dispatch({
        type: "xProductBom/CompleteLoadProductBom",
        data: (props.apiResult as any)?.result,
        key: (props as any).key,
    } as any);
};

export const ExecuteOnErrorCallback: HandlerType = props => {
    if (!props.apiResult.successful) {
        markSkipOnCompleteIfOnErrorIsSet(props);
        props.parameter.onError?.(props.apiResult.errorMessage!);
    }
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (props.apiResult.successful) {
        props.parameter.onSuccess?.(props.apiResult);
        const ids = Object.values((props.apiResult as any)?.result?.productIdLookup);
        props.dispatch(getProducts({ productIds: ids as string[], key: (props as any).key }));
    }
};

export const chain = [
    PopulateApiParameter,
    DispatchBeginLoadProductBom,
    GetBom,
    DispatchCompleteLoadProductBom,
    ExecuteOnErrorCallback,
    ExecuteOnSuccessCallback,
];
const getProductBom = createHandlerChainRunner(chain, "GetProductBom");

export default getProductBom;
